<script setup lang="ts">
import { computed, ref } from 'vue'
import { EBannerSize, EImagePosition, ESeoHeadingSize, type IBannerProps } from '../ts/types'
import { useImage } from '../../../composables/useImageUrl'
import { useBackground } from '../../../composables/useBackground'
import { getLinkTarget } from '../../../utils/linkTarget'
import { EBadgePlacement } from '@/ui/plushComponents/VBadge/types'
import { isDark } from '@/ui/helpers/utils'
import { VButtonVariants } from '@/ui/plushComponents/VButton/types'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'
import { EClickElement } from '@/cms-ui/utils/dataLayer'

const props = withDefaults(defineProps<IBannerProps>(), {
    size: EBannerSize.Medium,
    imagePlacement: EImagePosition.right,
    carousel: false,
    seoHeadingSize: ESeoHeadingSize.H3,
})

const banner = ref<HTMLElement | null>(null)

const bannerSize = {
    [EBannerSize.Large]: 'h-[560px]',
    [EBannerSize.Medium]: 'h-[380px]',
    [EBannerSize.Small]: 'h-[280px]',
    [EBannerSize.Hug]: '',
}

const { image: responsiveImage } = useImage(props.image, props.page)

const { backgroundStyles } = useBackground(props.page, props.background)

const bannerHovered = ref(false)
const bannerActive = ref(false)

const hoverClasses = {
    [VButtonVariants.PRIMARY]: '!text-brand-accent',
    [VButtonVariants.SECONDARY]: '!bg-comp-ghost-hover',
    [VButtonVariants.ACCENT]: '!bg-comp-accent-hover',
    [VButtonVariants.PLAIN]: '!bg-comp-ghost-hover',
}
const activeClasses = {
    [VButtonVariants.PRIMARY]: '!bg-comp-invert-pressed !text-brand-accent',
    [VButtonVariants.SECONDARY]: '!bg-comp-ghost-pressed',
    [VButtonVariants.ACCENT]: '!bg-comp-accent-pressed',
    [VButtonVariants.PLAIN]: '!bg-comp-ghost-pressed',
}
function inactive(): void {
    bannerActive.value = false
    bannerHovered.value = false
}
function active(): void {
    bannerActive.value = true
}
function hover(): void {
    bannerHovered.value = true
}

const placeholder = computed(() => {
    return (
        props.size === EBannerSize.Hug ||
        props?.imagePlacementMobile === EImagePosition.bottom ||
        props?.imagePlacementDesktop === EImagePosition.bottom ||
        (props.size === EBannerSize.Small && props.title && props.paragraph)
    )
})

const { openDrawer } = useDrawer()

function clickButton(): void {
    document.getElementById(`actionButton-${props.dataTest}`)?.click()
}
</script>

<template>
    <div
        ref="banner"
        class="relative overflow-hidden"
        :class="{ 'min-w-[80vw]': carousel }"
    >
        <div
            :style="backgroundStyles"
            class="rounded-container-lg cursor-pointer overflow-hidden"
            :class="[
                props.size && bannerSize[props.size],
                {
                    'max-md:flex-col': imagePlacementMobile === EImagePosition.bottom,
                    'md:flex-col': imagePlacementDesktop === EImagePosition.bottom,
                    'max-md:flex': imagePlacementMobile === EImagePosition.right,
                    'md:flex': imagePlacementDesktop === EImagePosition.right,
                },
                isDark(dark),
            ]"
            @mouseenter="hover"
            @mouseleave="inactive"
            @mousedown="active"
            @mouseup="inactive"
            @click="clickButton()"
        >
            <div
                class="relative z-10 flex flex-col justify-center px-6"
                :class="[
                    {
                        'pb-7': size === EBannerSize.Hug,
                        'w-3/5': image && imagePlacementMobile === EImagePosition.right,
                        'w-full': !image || imagePlacementMobile === EImagePosition.bottom,
                        'md:w-3/5': image && imagePlacementDesktop === EImagePosition.right,
                        'md:w-full': !image || imagePlacementDesktop === EImagePosition.bottom,
                    },
                ]"
            >
                <div class="rounded-ss-container-lg absolute left-[-0.1px] top-[-0.1px] overflow-hidden">
                    <VBadge
                        v-if="badge"
                        size="MEDIUM"
                        :dark="dark"
                        :data-test="`banner-badge-${dataTest}`"
                        :icon="badge?.icon"
                        :variant="badge?.visualVariant"
                        :placement="EBadgePlacement.CORNER"
                    >
                        {{ badge.label }}
                    </VBadge>
                </div>

                <div
                    v-if="placeholder"
                    class="h-[28px]"
                />

                <component
                    :is="seoHeadingSize"
                    v-if="title"
                    class="text-comp-high-contrast max-md:display-xs-smaller my-2 overflow-visible overflow-y-clip"
                    :class="[paragraph ? 'line-clamp-3' : 'line-clamp-4', titleSize]"
                >
                    {{ title }}
                </component>

                <div
                    v-if="paragraph"
                    class="text-comp-high-contrast text-paragraph-md my-2 line-clamp-4 font-medium"
                >
                    <div v-html-gpp="paragraph" />
                </div>

                <div
                    :class="!paragraph && !title ? 'absolute left-6 bottom-6 pr-6' : 'static pb-2'"
                    class="flex w-full"
                >
                    <BaseButton
                        v-if="button"
                        :id="`actionButton-${dataTest}`"
                        as="button"
                        class="mt-3"
                        :class="[
                            {
                                [hoverClasses[button?.visualVariant]]: bannerHovered && !bannerActive,
                                [activeClasses[button?.visualVariant]]: bannerHovered && bannerActive,
                            },
                            {
                                'max-md:max-w-[185%]':
                                    image && imagePlacementMobile === EImagePosition.right && (paragraph || title),
                                'max-md:max-w-full':
                                    (!image || imagePlacementMobile === EImagePosition.bottom) && (paragraph || title),
                                'md:max-w-[185%]':
                                    image && imagePlacementDesktop === EImagePosition.right && (paragraph || title),
                                'md:max-w-full':
                                    (!image || imagePlacementDesktop === EImagePosition.bottom) && (paragraph || title),
                                'max-w-[calc(100%-3rem)]': !paragraph && !title,
                                'max-w-[calc(100%-2rem)]': !paragraph && !title && carousel,
                            },
                        ]"
                        :data-test="`banner-button-${dataTest}`"
                        :variant="button?.visualVariant"
                        size="MEDIUM"
                        :leading-icon="button?.leadingIcon"
                        :trailing-icon="button?.icon"
                        :dark="dark"
                        :target="getLinkTarget(button?.action)"
                        :href="button?.action?.url"
                        @click.stop="[
                            useClickTracking({
                                label: button?.label,
                                clickPath: button?.action?.url || '',
                                clickElement: EClickElement.BUTTON,
                                componentName: title || 'BannersList',
                                componentCategory: 'BannersList',
                            }),
                            openDrawer(button?.action?.drawer, page),
                        ]"
                    >
                        {{ button?.label }}
                    </BaseButton>
                </div>
            </div>

            <div
                v-if="responsiveImage"
                class="z-0 flex justify-center"
                :class="{
                    'max-md:rounded-ee-container-lg max-md:rounded-es-container-lg max-md:absolute max-md:inset-x-0 max-md:bottom-0 max-md:items-end max-md:overflow-hidden':
                        imagePlacementMobile === EImagePosition.bottom,
                    'max-md:w-2/5 max-md:items-center': imagePlacementMobile === EImagePosition.right,
                    'max-md:h-2/5': imagePlacementMobile === EImagePosition.bottom && size !== EBannerSize.Hug,
                    'max-md:max-h-[560px] max-md:min-h-[80px]':
                        imagePlacementMobile === EImagePosition.bottom && size === EBannerSize.Hug,
                    'md:rounded-ee-container-lg md:rounded-es-container-lg md:absolute md:inset-x-0 md:bottom-0 md:items-end md:overflow-hidden':
                        imagePlacementDesktop === EImagePosition.bottom,
                    'md:w-2/5 md:items-center': imagePlacementDesktop === EImagePosition.right,
                    'md:h-2/5': imagePlacementDesktop === EImagePosition.bottom && size !== EBannerSize.Hug,
                    'md:max-h-[560px] md:min-h-[80px]':
                        imagePlacementDesktop === EImagePosition.bottom && size === EBannerSize.Hug,
                }"
            >
                <BaseImage
                    class="max-h-full max-w-full"
                    picture-classes="size-full"
                    :image-data="responsiveImage"
                    :alt="image?.altText"
                />
            </div>
        </div>
    </div>
</template>
